/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { forwardRef, useImperativeHandle, useRef, useState } from "react";

import { Editor } from "@tinymce/tinymce-react";
import { Editor as TinyMCEEditor } from "tinymce";
import Text from "components/ui/Text";

const SCRIPT_SRC = `/tinymce/tinymce.min.js`;

export const addEditorTagPlaceholder = (editor: TinyMCEEditor, tag: string) => {
  editor.insertContent(`&nbsp;<span>${tag}</span>&nbsp;`);
};

type RichTextEditorProps = {
  onChange?: (content: string) => void;
  value?: string;
  disabled?: boolean;
  onAddButton?: (editor: TinyMCEEditor) => void;
  id?: string;
  height?: number | string;
  maxLength?: number;
  onFocus?: () => void;
  onBlur?: () => void;
  autoResize?: boolean;
  maxHeight?: number;
  useModalScrollbarTheme?: boolean;
};

const RichTextEditor = forwardRef<any, RichTextEditorProps>(
  (
    {
      onChange,
      value,
      disabled,
      onAddButton,
      id,
      height = 360,
      maxLength,
      onFocus,
      onBlur,
      autoResize,
      maxHeight,
      useModalScrollbarTheme,
      // ...rest
    },
    ref,
  ) => {
    const [length, setLength] = useState(0);

    const handleEditorChange = (content: string, editor: TinyMCEEditor) => {
      const length = editor.getContent({ format: "text" }).length;
      if ((maxLength && length <= maxLength) || !maxLength) {
        setLength(length);
        onChange?.(content);
      } else {
        const container = editor.editorContainer;
        container.style.border = "2px solid var(--alert)";

        setTimeout(() => {
          container.style.border = "";
        }, 500);
      }
    };

    const divRef = useRef<HTMLDivElement | null>(null);

    useImperativeHandle(ref, () => {
      return {
        focus() {
          divRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        },
      };
    }, []);

    const handleBeforeAddUndo = (evt, editor) => {
      const length = editor.getContent({ format: "text" }).length;
      if (length > maxLength) {
        evt.preventDefault();
      }
    };

    const baseContentStyle = disabled ? "body { color: #f9f9f980; }" : "";

    const resolvedScrollbarThumbColor =
      typeof window !== "undefined"
        ? getComputedStyle(document.documentElement)
            .getPropertyValue("--component-button-tertiary-default-border")
            .trim() || "#404040"
        : "#404040";

    const modalScrollbarStyle = `html::-webkit-scrollbar, body::-webkit-scrollbar { width: 4px; height: 4px; } html::-webkit-scrollbar-track, body::-webkit-scrollbar-track { background: transparent; } html::-webkit-scrollbar-thumb, body::-webkit-scrollbar-thumb { background: ${resolvedScrollbarThumbColor}; } html::-webkit-scrollbar-thumb, body::-webkit-scrollbar-thumb { border-radius: 9999px; } html, body { scrollbar-width: thin; scrollbar-color: ${resolvedScrollbarThumbColor} transparent; }`;

    const contentStyle = useModalScrollbarTheme
      ? `${baseContentStyle} ${modalScrollbarStyle}`
      : baseContentStyle;

    const plugins = `preview importcss searchreplace autolink autosave save directionality code visualblocks visualchars fullscreen image link media codesample table charmap pagebreak nonbreaking insertdatetime advlist lists wordcount help charmap quickbars${autoResize ? " autoresize" : ""}`;

    return (
      <div ref={divRef} className="relative">
        <Editor
          id={id}
          key={disabled ? "disabled" : "enabled"}
          tinymceScriptSrc={SCRIPT_SRC}
          disabled={disabled}
          onInit={(_evt, editor) => {
            setLength(editor.getContent({ format: "text" }).length);

            if (editor && ref) {
              if (editor?.hasOwnProperty("editor")) {
                // @ts-ignore
                ref.current = editor;
              } else {
                // @ts-ignore
                ref.current = editor;
              }
            }
          }}
          onBeforeAddUndo={maxLength ? handleBeforeAddUndo : undefined}
          value={value || ""}
          init={{
            skin_url: "/blink_skin/ui",
            content_css: ["/blink_skin/content/content.css"],
            content_style: contentStyle,
            ...(!autoResize && {
              height,
            }),
            ...(autoResize && {
              min_height: typeof height === "number" ? height : undefined,
              max_height: maxHeight,
              resize: false,
              statusbar: false,
              autoresize_bottom_margin: 0,
            }),
            branding: false,
            directionality: "ltr",
            promotion: false,
            plugins,
            menubar: "file edit view insert format tools table",
            // toolbar_sticky: true,
            setup: (editor) => {
              if (onAddButton) {
                editor.ui.registry.addButton("ADD_BTN", {
                  icon: "plus",
                  tooltip: "Add tag",
                  onAction(_) {
                    onAddButton(editor);
                  },
                });
              }
            },
            // Don't add `fullscreen` to the toolbar. It's buggy.
            toolbar: `undo redo | bold italic underline strikethrough |${
              onAddButton && " ADD_BTN |"
            } fontfamily fontsize blocks | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist | forecolor backcolor removeformat | pagebreak | charmap | preview save print | link codesample | ltr rtl`,
            toolbar_mode: "sliding",
          }}
          onEditorChange={handleEditorChange}
          // Doesn't work properly
          // onBlur={(editor) => {
          //   const content = editor.target.getContent();
          //   rest.onBlur?.(content);
          // }}
          test-id="8yV2TXKB"
          onFocus={onFocus}
          onBlur={onBlur}
        />

        {maxLength && (
          <Text
            as="span"
            className="text-[12px] ml-auto text-[#7a7a7a] absolute right-0.5 -bottom-5"
          >
            {length ?? 0} / {maxLength}
          </Text>
        )}
      </div>
    );
  },
);

export default RichTextEditor;
